/* Read layer for the offline snapshot.
 *
 * Used as a fallback by src/lib/api.js whenever a GET request fails because
 * the device is offline. The snapshot itself is populated by the SW when an
 * OFFLINE_PREFETCH push arrives (see public/service-worker.js).
 *
 * Routes that map to snapshot fields:
 *   GET /trips                                 → snapshot.calendar.items
 *   GET /trips/{id}                            → snapshot.trip_details[id]
 *   GET /cbp/airports                          → snapshot.cbp_airports
 *   GET /aip-mx/airports                       → snapshot.aip_mx_airports
 *   GET /faa-nasr/airports                     → snapshot.faa_nasr_airports
 *   GET /trips/{id}/messages?channel=…         → snapshot.trip_chats[id] (filtered)
 *   GET /expenses?flight_id={id}               → snapshot.trip_expenses[id]
 *
 * Anything outside this list returns null and the api.js layer surfaces the
 * normal network error to the caller (Altiv principle: no silent failures).
 */
import { getSnapshot, getMeta } from "./offline-db";

const TRIP_DETAIL_RE = /^\/trips\/([^/?]+)$/;
const TRIP_MESSAGES_RE = /^\/trips\/([^/?]+)\/messages/;
const EXPENSES_LIST_RE = /^\/expenses(\?|$)/;

/** Match a request URL/path against the snapshot. Returns the slice of data
 * the endpoint would have returned, or null if we don't have it cached. */
export async function tryServeFromCache({ method, url }) {
  if ((method || "get").toLowerCase() !== "get") return null;
  const snapshot = await getSnapshot();
  if (!snapshot) return null;

  // Strip leading /api if the axios baseURL didn't already.
  const path = url.replace(/^https?:\/\/[^/]+/, "").replace(/^\/api/, "");

  // /trips → calendar items in the prefetched window. Real /trips returns
  // a richer shape but the calendar grid only needs these fields.
  if (path === "/trips" || path.startsWith("/trips?")) {
    return snapshot.calendar?.items || [];
  }

  const tripDetailMatch = path.match(TRIP_DETAIL_RE);
  if (tripDetailMatch) {
    const tripId = tripDetailMatch[1];
    return snapshot.trip_details?.[tripId] || null;
  }

  const tripMessagesMatch = path.match(TRIP_MESSAGES_RE);
  if (tripMessagesMatch) {
    const tripId = tripMessagesMatch[1];
    const all = snapshot.trip_chats?.[tripId] || [];
    // Caller may have filtered by channel — replicate that here.
    const channelMatch = path.match(/[?&]channel=([^&]+)/);
    if (channelMatch) {
      const ch = decodeURIComponent(channelMatch[1]);
      return all.filter((m) => m.channel === ch);
    }
    return all;
  }

  if (EXPENSES_LIST_RE.test(path)) {
    // The snapshot groups expenses by TRIP id (see backend routes/offline.py),
    // but callers query by flight_id (= leg id) — e.g. the flight-detail Gastos
    // tab requests /expenses?flight_id={legId}. Looking up trip_expenses[legId]
    // directly always missed (keys are trip ids), so per-flight expenses showed
    // empty offline. Flatten across trips and filter by the expense's own
    // flight_id instead; with no filter, aggregate everything (gastos history).
    const all = [];
    Object.values(snapshot.trip_expenses || {}).forEach((arr) => {
      if (Array.isArray(arr)) all.push(...arr);
    });
    const flightMatch = path.match(/[?&]flight_id=([^&]+)/);
    if (flightMatch) {
      const fid = decodeURIComponent(flightMatch[1]);
      return all.filter((e) => e.flight_id === fid);
    }
    return all;
  }

  if (path === "/cbp/airports" || path.startsWith("/cbp/airports?")) {
    return snapshot.cbp_airports || { count: 0, items: [] };
  }
  if (path === "/aip-mx/airports" || path.startsWith("/aip-mx/airports?")) {
    return snapshot.aip_mx_airports || { count: 0, items: [] };
  }
  if (path === "/faa-nasr/airports" || path.startsWith("/faa-nasr/airports?")) {
    return snapshot.faa_nasr_airports || { count: 0, items: [] };
  }

  return null;
}

export async function getSnapshotMeta() {
  return {
    last_snapshot_at: await getMeta("last_snapshot_at"),
    last_snapshot_user_id: await getMeta("last_snapshot_user_id"),
  };
}

export async function hasSnapshot() {
  return !!(await getSnapshot());
}
